import {ContentCopy} from "@mui/icons-material";
import {AccordionSummary, Box, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";

import {GqlPermissions, GqlProtocolVisit} from "~/__generated__";
import { formatCents, formatSeconds } from "~/modules/app/util/formatters";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";

import {PRECIPITATION_LEVELS_IN_ORDER_OF_SEVERITY} from "../../constants";
import ProtocolVisitConditionsSummary from "../ProtocolVisitConditionsSummary";
import ProtocolVisitTimeslotSummary from "../ProtocolVisitTimeslotSummary";

type Props = {
    visit: GqlProtocolVisit;
    allVisits: GqlProtocolVisit[];
    onCopy: () => void;
    copyLoading?: boolean;
};

export default function VisitSummary({ visit, allVisits, onCopy, copyLoading }: Props) {
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);
    const { t } = useTranslation("protocol");

    return (
        <AccordionSummary component="div" id="panel-header" aria-controls="panel-content">
            <Stack direction="row" gap={2} width={1}>
                <Typography variant="h3">#{visit.number}</Typography>
                <Box flex={1}>
                    <Typography variant="h6" display="block">
                        {t("visitSummary.timeslots")}
                    </Typography>
                    <Typography variant="caption" display="block">
                        {t("visitSummary.duration")}: <strong>{formatSeconds(visit.minDurationInSeconds)}</strong>
                    </Typography>
                    {visit.timeslots && <ProtocolVisitTimeslotSummary timeslots={visit.timeslots} />}
                    <Typography variant="caption" display="block">
                        {t("visitSummary.costPerHour")}: <strong>{formatCents(visit.pricePerHourInCents)}</strong>
                    </Typography>
                    {visit.name && (
                        <Typography variant="caption" display="block">
                            {t("visitSummary.name")}: <strong>{visit.name}</strong>
                        </Typography>
                    )}
                </Box>
                <Box flex={1}>
                    <Typography variant="h6" display="block">
                        {t("visitSummary.weatherConditions")}
                    </Typography>
                    <Typography variant="caption" display="block">
                        {t("visitSummary.temperature")} <strong>{visit.minTemperature}°C</strong> -{" "}
                        <strong>{visit.maxTemperature}°C</strong>
                    </Typography>
                    <Typography variant="caption" display="block">
                        {t("visitSummary.maxWindForce")} <strong>{visit.maxWindForce} Bft</strong>
                    </Typography>
                    <Typography variant="caption" display="block">
                        {t("visitSummary.maxPrecipitation")}{" "}
                        <strong>
                            {t(
                                `protocolVisitsWeatherEditor.precipitation.${PRECIPITATION_LEVELS_IN_ORDER_OF_SEVERITY[visit.maxPrecipitation]}`,
                            )}
                        </strong>
                    </Typography>
                </Box>
                <Box flex={1}>
                    <Typography variant="h6">{t("visitSummary.conditions")}</Typography>
                    {visit.condition && (
                        <ProtocolVisitConditionsSummary condition={visit.condition} allVisits={allVisits} />
                    )}
                </Box>
                {isAdmin && (
                    <Box>
                        <Tooltip title={t("visitSummary.copyVisit")}>
                            <IconButton
                                component="button"
                                onClick={e => {
                                    e.stopPropagation();
                                    onCopy();
                                }}
                                disabled={copyLoading}
                                size="small"
                            >
                                <ContentCopy />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Stack>
        </AccordionSummary>
    );
}
